/**
 * Delete one share snapshot (plan Task 7.16): a plain confirm (no typed name) naming the snapshot's local time and
 * "Not recoverable". It runs in the app-level confirm dialog, whose latch keeps one delete in flight and keeps the
 * dialog open on a refusal; the refusal (for Entra, main's role sentence with its chip) shows inside the confirm.
 * On success the tabs rooted at that snapshot close and its pins go. One enum-only event per answered attempt.
 */
import { create, type StoreApi, type UseBoundStore } from 'zustand';
import type { FileShareProtocolState, StorageOperationError } from '../../../../../types/azureStorage';
import { openConfirmDialog } from '../../../common/components/confirm/confirmDialogStore';
import { toStorageOperationError } from '../../services/azureStorageApi';
import { formatStorageTimestamp } from '../../utils/storageItemRows';
import type { ShareActionSource } from '../namedShareActions';
import { startShareRequest } from '../shareApi';
import { closeShareSurfaces, shareActionOutcome, trackShareAction } from '../shareActions';
import { ShareErrorNotice } from './ShareErrorNotice';

type Session = UseBoundStore<StoreApi<{ error?: StorageOperationError }>>;
export type SnapshotDeleteArgs = {
  connectionId: string; share: string; snapshot: string; source: ShareActionSource; protocol?: FileShareProtocolState; onDeleted?: () => void;
};

function SnapshotDeleteSummary({ share, snapshot, session }: { share: string; snapshot: string; session: Session }) {
  const error = session((state) => state.error);
  return (
    <span className="block space-y-2" data-testid="snapshot-delete-summary">
      <span className="block">Deletes the snapshot of {share} taken {formatStorageTimestamp(snapshot)}. Not recoverable.</span>
      {error && <ShareErrorNotice error={error} testId="snapshot-delete-error" />}
    </span>
  );
}

export function openSnapshotDeleteConfirm(args: SnapshotDeleteArgs): void {
  const session: Session = create(() => ({}));
  const meta = { source: args.source, protocol: args.protocol ?? 'unknown' };
  openConfirmDialog({
    title: 'Delete snapshot',
    description: <SnapshotDeleteSummary share={args.share} snapshot={args.snapshot} session={session} />,
    confirmLabel: 'Delete snapshot',
    variant: 'destructive',
    onConfirm: async () => {
      session.setState({ error: undefined });
      const result = await startShareRequest('shareSnapshotDelete', { connectionId: args.connectionId, resource: { kind: 'share', share: args.share, snapshot: args.snapshot } }).promise;
      trackShareAction('snapshot_delete', shareActionOutcome(result.ok ? undefined : result.error), meta);
      if (!result.ok) throw result.error;
      await closeShareSurfaces(args.connectionId, args.share, args.snapshot);
      args.onDeleted?.();
    },
    onError: (error) => session.setState({ error: toStorageOperationError(error) }),
  });
}
